<template>
  <Head title="Admin - Task Usage History"></Head>
  <s-page-block size="md">
    <s-page-header heading="Assignment Task History" class="pb-4">
      <template #actions>
        <s-btn
          v-if="assignment.id"
          color="blue-light"
          icon="book-edit"
          :href="`/courses/${assignment.course.id}/assignments/${assignment.id}`"
        >
          Assignment: {{ assignment.name }}
        </s-btn>
        <s-btn
          v-if="task.id"
          color="blue-light"
          icon="eye"
          :href="route('admin.tasks.show', {task: task.id})"
        >
          Task Preview
        </s-btn>
      </template>
    </s-page-header>
    <section class="mt-4">
      <div class="card padded mt-8">
        <s-badge-group class="mb-4">
          <s-badge icon="account" color="white">
            {{ studentCount + ' Students' }}
          </s-badge>
          <s-badge icon="pencil" color="white">
            {{ responseCount + ' Responses' }}
          </s-badge>
        </s-badge-group>
        <task-mistake-labels-pie-chart v-if="clusterData" :cluster-data="clusterData" />
        <span v-else>No Student Mistake Labels Data Exists for this Task</span>
        <div class="mb-4 mt-4 flex justify-end">
          <s-label class="mb-1.5">Clusters to evaluate:</s-label>
          <s-multi-select
            :options="options"
            placeholder="Select number of clusters to evaluate."
            multiple
            v-model="clustersToEvaluate"
            class="ml-4"
          ></s-multi-select>
          <s-btn
            v-if="task.id && assignment.id"
            color="blue-light"
            class="ml-4"
            icon="refresh"
            method="post"
            :href="
              route('admin.ai-analytics.tasks.assignments.new-clusters', {
                task: task.id,
                assignment: assignment.id,
                clustersToEvaluate: clustersToEvaluate,
              })
            "
          >
            Re-run Clustering
          </s-btn>
        </div>
      </div>
      <div class="card padded mt-8">
        <task-grade-averages-multiple-step-line-chart
          v-if="rollingAverages.length > 0"
          :rolling-averages="rollingAverages"
        />
        <span v-else>No Grading Data Exists for this Task</span>
      </div>
      <div class="card padded mt-8">
        <task-learning-rate-charts
          v-if="gradesByAttempts"
          :grades-by-attempts="gradesByAttempts"
          :task="task"
          :assignment="assignment"
        />
        <span v-else>No Learning Rate Exists for this Task</span>
      </div>
      <div class="card padded mt-8">
        <task-token-usage-history-line-chart
          v-if="tokenUsage.length > 0"
          :token-usage="tokenUsage"
        />
        <span v-else>No Token Usage Tracking Exists for this Task</span>
      </div>
    </section>
  </s-page-block>
</template>

<script setup lang="ts">
import {route} from 'ziggy-js';
import {Head} from '@inertiajs/vue3';
import {useBreadcrumbs} from '../../../../composables/useBreadcrumbs';
import {Task} from '../../../../types/entities/tasks';
import SBtn from '../../../../design-system/SBtn.vue';
import {Assignment} from '../../../../types/entities/assignment';
import TaskTokenUsageHistoryLineChart from '../Task/TaskAssignmentTokenUsageHistoryLineChart.vue';
import TaskGradeAveragesMultipleStepLineChart from '../Task/TaskGradeAveragesMultipleStepLineChart.vue';
import TaskLearningRateCharts from '../Task/TaskLearningRateCharts.vue';
import SPageBlock from '../../../../design-system/SPageBlock.vue';
import SPageHeader from '../../../../design-system/SPageHeader.vue';
import TaskMistakeLabelsPieChart from '../Task/TaskMistakeLabelsPieChart.vue';
import StudentMistakeLabelClustersResults = App.DTOs.Analytics.StudentMistakeLabelClustersResults;
import {ref} from 'vue';
import SInputField from '../../../../design-system/SInputField.vue';
import SMultiSelect from '../../../../design-system/controls/SMultiSelect.vue';
import TaskText from '../../../../text/TaskText.vue';
import SLabel from '../../../../design-system/SLabel.vue';
import SBadge from '../../../../design-system/SBadge.vue';
import SBadgeGroup from '../../../../design-system/SBadgeGroup.vue';
import {Option} from '../../../../design-system/controls/Option.js';

const props = defineProps<{
  task: Task;
  assignment: Assignment;
  tokenUsage: {
    day: string;
    totalTokens: number;
    totalImageTokens: number;
    avgInputTokensPerAPIRequest: number;
    avgOutputTokensPerAPIRequest: number;
    avgInputTokensPerImageAPIRequest: number;
    avgOutputTokensPerImageAPIRequest: number;
  }[];
  rollingAverages: {
    day: string;
    new_grades: number;
    avg_final_grade: number;
    avg_accepted_grade: number;
    avg_grade_pre_override: number;
    avg_override_grade_difference: number;
    pie_data: {name: string; value: number}[];
  }[];
  gradesByAttempts: {
    [maxAttempts: string]: {
      example_user_id: number;
      number_of_students: number;
      final_avg_grade: number;
      attempt_data: {
        attempt_number: number;
        avg_grade: number;
      }[];
    };
  };
  clusterData: StudentMistakeLabelClustersResults;
  responseCount: number;
  studentCount: number;
}>();

const clustersToEvaluate = ref([]);
const maxClusters = Math.min(10, props.responseCount);
const options: Option[] = Array.from({length: maxClusters - 2}, (_, i) => {
  const value = i + 3;
  return {label: String(value), value};
});

useBreadcrumbs([
  {label: 'Ai Analytics', href: route('admin.ai-analytics.index')},
  {label: 'Tasks', href: route('admin.ai-analytics.tasks.index')},
  {label: props.task.title, href: route('admin.ai-analytics.tasks.show', {task: props.task.id})},
]);
</script>
